import React, { useEffect, useRef, useState } from 'react';
import CreateQuestions from '../../../component/EliteGame/CreateQuestions';
import QuestionsList from '../../../component/EliteGame/QuestionsList';
import {
  createQuestion,
  deleteQuestion,
  GetMainCategories,
  GetSubCategories,
  getQuestionsByQuizType,
  updateQuestion
} from '../../../api/eliteGameApi';
import { ELITE_QUIZ_TYPE } from '../../../utils/constants';
import { useEliteGames } from '../../../context/EliteGamesContext';
import ViewQuestionModal from '../../../component/EliteGame/ViewQuestionModal';
import { errorToast, successToast } from '../../../utils/helper';
import DeletePopup from '../../../component/common/modals/DeletePopup';

const GuessWordManageQuestions = () => {
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [refresh, setRefresh] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { setLoading } = useEliteGames();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [questionId, setQuestionId] = useState(null);
  const [open, setOpen] = useState(false);
  const formRef = useRef(null);

  // Function to handle viewing a question
  const handleView = (question) => {
    setViewQuestion(question);
    setViewModalOpen(true);
  };

  // Function to fetch main categories for Guess The Word
  const getMainCategories = async () => {
    setLoading(true);
    try {
      const data = await GetMainCategories({ quizType: ELITE_QUIZ_TYPE.GUESS_THE_WORD }, true);
      setMainCategories(data.data || []);
    } catch (error) {
      errorToast('Failed to fetch main categories', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuestions = async (filter = {}) => {
    setLoading(true);
    try {
      const data = await getQuestionsByQuizType(ELITE_QUIZ_TYPE.GUESS_THE_WORD, {
        page,
        limit,
        ...filter
      });
      setQuestions(data || []);
    } catch (error) {
      errorToast('Failed to fetch questions', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMainCategories();
  }, []);

  useEffect(() => {
    getQuestions();
  }, [refresh, page, limit]);

  const getSubCategories = async (mainCategoryId) => {
    setLoading(true);
    try {
      const data = await GetSubCategories(
        { categoryId: mainCategoryId, quizType: ELITE_QUIZ_TYPE.GUESS_THE_WORD },
        true
      );
      setSubCategories(data.data || []);
    } catch (error) {
      errorToast('Failed to fetch sub categories', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (question) => {
    setSelectedQuestion(question);
    if (question.mainCategory) {
      getSubCategories(question.mainCategory);
    }
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Handle filter action for questions
  const handleFilter = (filter) => {
    getQuestions(filter);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const formData = {
        ...data,
        quizType: ELITE_QUIZ_TYPE.GUESS_THE_WORD,
        correctAnswer: data.answer // For guess the word, the answer is the word/phrase itself
      };

      // If a question is selected, update it; otherwise, create a new question
      if (selectedQuestion) {
        const response = await updateQuestion(formData, selectedQuestion._id);
        successToast('Question updated successfully', response.message);
        setSelectedQuestion(null); // Clear selected question after update
      } else {
        // Create a new question
        const response = await createQuestion(formData);
        setRefreshFlag((prev) => prev + 1);
        successToast('Question created successfully', response.message);
      }
      getQuestions(); // Refresh questions list after creating a new question
      setSelectedQuestion(null); // Clear selected question after submission
    } catch (error) {
      errorToast('Error creating/updating question:', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };

  // Handle delete action for a question
  const handleDelete = async () => {
    setLoading(true);
    try {
      // Call API to delete question
      await deleteQuestion(questionId);
      successToast('Question deleted successfully');
      setRefresh(true); // Refresh questions list after deletion
    } catch (error) {
      errorToast('Error deleting question:', error);
    } finally {
      setLoading(false);
    }
    setRefresh(!refresh);
  };

  // Handle refresh action for questions
  const handleRefresh = () => {
    // Reset page to 1 and refresh questions
    setPage(1);
    getQuestions(); // Fetch fresh data
  };
  return (
    <div>
      <div ref={formRef}>
        <CreateQuestions
          onSubmit={onSubmit}
          name={'Guess The Word'}
          quizType={ELITE_QUIZ_TYPE.GUESS_THE_WORD}
          mainCategories={mainCategories}
          getSubCategories={getSubCategories}
          subCategories={subCategories}
          existingQuestion={selectedQuestion}
          resetTrigger={refreshFlag}
        />
      </div>
      <div className="bg-white rounded-lg shadow p-6 m-4">
        <div className="mb-2">
          <span className="text-2xl font-bold text-black-600">Questions for Guess The Word</span>
        </div>
        <hr className="mb-6" />
        <QuestionsList
          quizType={ELITE_QUIZ_TYPE.GUESS_THE_WORD}
          questions={questions}
          categoryList={mainCategories}
          subCategoryList={subCategories}
          getSubCategories={getSubCategories}
          onEdit={handleEdit}
          page={page}
          limit={limit}
          setLimit={setLimit}
          onFilter={handleFilter}
          setPage={setPage}
          onDelete={(id) => {
            setQuestionId(id);
            setOpen(true);
          }}
          onView={handleView}
          onRefresh={handleRefresh}
        />
        {DeletePopup({
          title: 'Confirm Deletion',
          open,
          setOpen,
          message:
            'Are you sure you want to delete this question? All related data will also be deleted.',
          setDelete: handleDelete
        })}
      </div>
      <ViewQuestionModal
        questions={viewQuestion}
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
      />
    </div>
  );
};

export default GuessWordManageQuestions;
